import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { formatDate } from '@angular/common';
import { Subscription } from 'rxjs';
import { Event as GsEvent } from '../../../private/events/models/event.model';
import { EventsService } from '../../../private/events/services/events.service';
import {
  GsSelectableListComponent,
  SelectionMeta,
} from '../../ui/selectable-list/gs-selectable-list.component';

export { SelectionMeta };

interface SelectableEvent {
  id: number;
  description: string;
  subtitle: string;
  code: string;
  date: string;
}

@Component({
  selector: 'gs-event-selector',
  templateUrl: './gs-event-selector.component.html',
  styleUrls: ['./gs-event-selector.component.css'],
  imports: [FormsModule, GsSelectableListComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GsEventSelectorComponent),
      multi: true,
    },
  ],
})
export class GsEventSelectorComponent
  implements ControlValueAccessor, OnChanges, OnInit, OnDestroy
{
  @Input() mode: 'single' | 'multiple' = 'multiple';
  @Input() pageSize = 10;
  @Input() searchable = true;
  @Input() disabledIds: number[] = [];
  @Input() initialSelected?: GsEvent[];

  @Output() selectionMeta = new EventEmitter<SelectionMeta>();

  public items: SelectableEvent[] = [];
  public currentPage = 1;
  public totalItems = 0;
  public isSelectingAll = false;
  public isLoading = false;
  public selectedIds: number[] = [];
  public isDisabled = false;

  private subscriptions: Subscription[] = [];
  private hasWrittenValue = false;
  private selectAllRequest = 0;

  private onChange: (ids: number[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  private eventsService = inject(EventsService);

  ngOnChanges(changes: SimpleChanges): void {
    const initialSelectedChange = changes['initialSelected'];

    if (initialSelectedChange && !this.hasWrittenValue) {
      this.selectedIds = this.getInitialSelectedIds();
      this.emitSelectionMeta();
    }
  }

  ngOnInit(): void {
    this.loadPage(1);
  }

  ngOnDestroy(): void {
    this.selectAllRequest += 1;
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  writeValue(value: number[] | null): void {
    const normalized = this.normalizeIds(value ?? []);

    this.selectedIds =
      normalized.length === 0 && !this.hasWrittenValue
        ? this.getInitialSelectedIds()
        : normalized;
    this.hasWrittenValue = true;
    this.emitSelectionMeta();
  }

  registerOnChange(fn: (ids: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  public onSelectionChange(ids: number[]): void {
    this.setSelection(ids);
    this.onTouched();
  }

  public onPageChange(page: number): void {
    this.loadPage(page);
  }

  public selectAll(checked: boolean): void {
    this.selectAllRequest += 1;
    const requestId = this.selectAllRequest;

    if (!checked) {
      this.isSelectingAll = false;
      this.isLoading = false;
      this.setSelection([]);
      this.onTouched();
      return;
    }

    this.isSelectingAll = true;
    this.isLoading = true;
    this.loadAllEventIds(1, [], requestId);
  }

  private loadPage(page: number): void {
    this.currentPage = page;
    this.isLoading = true;

    const pageSubscription = this.eventsService
      .getAll$(page, { future: true, perPage: this.pageSize })
      .subscribe({
        next: response => {
          this.isLoading = false;
          const events = response.data ?? [];
          const metadata = response.metadata;

          this.items = events.map(event => this.toSelectableEvent(event));
          this.currentPage = metadata?.currentPage ?? page;
          this.totalItems = metadata?.total ?? events.length;
          this.emitSelectionMeta();
        },
        error: () => {
          this.isLoading = false;
        },
      });

    this.subscriptions.push(pageSubscription);
  }

  private loadAllEventIds(
    page: number,
    selectedIds: number[],
    requestId: number
  ): void {
    const pageSubscription = this.eventsService
      .getAll$(page, { future: true, perPage: this.pageSize })
      .subscribe({
        next: response => {
          if (requestId !== this.selectAllRequest) {
            return;
          }

          const events = response.data ?? [];
          const nextSelectedIds = [
            ...selectedIds,
            ...events.map(event => event.id),
          ];
          const metadata = response.metadata;

          if (metadata?.hasMorePages) {
            const currentPage = metadata.currentPage ?? page;
            this.loadAllEventIds(currentPage + 1, nextSelectedIds, requestId);
            return;
          }

          this.isSelectingAll = false;
          this.isLoading = false;
          this.totalItems = metadata?.total ?? nextSelectedIds.length;
          this.setSelection(nextSelectedIds);
          this.onTouched();
        },
        error: () => {
          if (requestId === this.selectAllRequest) {
            this.isSelectingAll = false;
            this.isLoading = false;
          }
        },
      });

    this.subscriptions.push(pageSubscription);
  }

  private setSelection(ids: number[]): void {
    this.selectedIds = this.normalizeIds(ids);
    this.onChange([...this.selectedIds]);
    this.emitSelectionMeta();
  }

  private emitSelectionMeta(): void {
    const selectedTotal = this.selectedIds.length;
    const totalAvailable = this.totalItems;
    const isAllSelected =
      totalAvailable > 0 && selectedTotal === totalAvailable;

    this.selectionMeta.emit({ totalAvailable, selectedTotal, isAllSelected });
  }

  private getInitialSelectedIds(): number[] {
    const selectedEvents = this.initialSelected ?? [];
    return selectedEvents.map(event => event.id);
  }

  private normalizeIds(ids: number[]): number[] {
    const numericIds = ids
      .map(id => Number(id))
      .filter(id => Number.isFinite(id));
    return Array.from(new Set(numericIds));
  }

  private formatEventDate(value: string): string {
    if (!value) {
      return '';
    }

    try {
      return formatDate(value, 'mediumDate', 'en-US');
    } catch {
      return '';
    }
  }

  private toSelectableEvent(event: GsEvent): SelectableEvent {
    return {
      id: event.id,
      description: event.description,
      subtitle: `${event.code} - ${this.formatEventDate(event.date)}`,
      code: event.code,
      date: event.date,
    };
  }
}
